'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { MousePointer2 } from '@/components/ui/icons'

interface Cursor {
  id: string
  name: string
  color: string
  x: number // percentage
  y: number // percentage
  delay: number
  parallaxSpeed: number // How much the cursor moves on scroll (higher = more movement)
}

const CURSORS: Cursor[] = [
  { id: '1', name: 'Sarah', color: '#F472B6', x: 15, y: 25, delay: 0, parallaxSpeed: 0.15 }, // Pink - on screenshot
  { id: '2', name: 'Marcus', color: '#34D399', x: 75, y: 45, delay: 0.2, parallaxSpeed: -0.1 }, // Green - on screenshot
  { id: '3', name: 'Alex', color: '#60A5FA', x: -4, y: 65, delay: 0.4, parallaxSpeed: 0.2 }, // Blue - outside left
  { id: '4', name: 'Yuki', color: '#A78BFA', x: 101, y: 75, delay: 0.6, parallaxSpeed: -0.12 }, // Purple - outside right
]

// Special wandering cursor that moves around more dramatically
const WANDERER = {
  id: 'wanderer',
  name: 'Luna',
  color: '#FACC15', // Bright yellow for visibility
  delay: 0.8,
}

export function CollaborativeCursors() {
  const [isVisible, setIsVisible] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const [scrollOffset, setScrollOffset] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(0)

  // Smooth scroll tracking with RAF
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const elementCenter = rect.top + rect.height / 2
    const viewportCenter = windowHeight / 2
    const offset = (elementCenter - viewportCenter) / windowHeight

    setScrollOffset(offset * 100) // Convert to percentage-like value
  }, [])

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(handleScroll)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    handleScroll() // Initial calculation

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [handleScroll])

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640) // sm breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            setIsVisible(true)
            setHasAnimated(true)
          }
        })
      },
      { threshold: 0.3 },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [hasAnimated])

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20 overflow-visible"
      style={isMobile ? { left: '-20px', right: '-20px', width: 'calc(100% + 40px)' } : undefined}
    >
      {CURSORS.map(cursor => {
        // Calculate parallax offset for this cursor
        const parallaxY = scrollOffset * cursor.parallaxSpeed
        const parallaxX = scrollOffset * cursor.parallaxSpeed * 0.3 * (parseInt(cursor.id) % 2 === 0 ? 1 : -1)

        // On mobile, adjust Alex's position to prevent clipping (x: -4% gets cut off)
        // Move to 8% on mobile so the label (which extends left with left-4 = 16px) is fully visible
        const isAlex = cursor.id === '3'
        const leftPosition = isAlex && isMobile ? 8 : cursor.x

        return (
          <div
            key={cursor.id}
            className={`absolute transition-opacity duration-1000 ${isAlex ? 'z-30' : ''}`}
            style={{
              left: `${leftPosition}%`,
              top: `${cursor.y}%`,
              opacity: isVisible ? 1 : 0,
              transform: isVisible
                ? `translate(${parallaxX}px, ${parallaxY}px) scale(1)`
                : 'translate(0, 20px) scale(0.5)',
              transition: isVisible
                ? 'opacity 1s ease-out, transform 0.15s ease-out'
                : 'opacity 1s ease-out, transform 1s ease-out',
              transitionDelay: isVisible ? `${cursor.delay}s, 0s` : `${cursor.delay}s`,
            }}
          >
            <div
              className="animate-cursor-float relative"
              style={{
                animationDelay: `${cursor.delay * 2}s`,
                ['--float-x' as string]: `${(parseInt(cursor.id) % 2 === 0 ? 1 : -1) * 8}px`,
                ['--float-y' as string]: `${(parseInt(cursor.id) % 2 === 0 ? -1 : 1) * 6}px`,
              }}
            >
              <MousePointer2
                width={20}
                height={20}
                fill={cursor.color}
                color={cursor.color}
                className="drop-shadow-lg"
                style={{ filter: `drop-shadow(0 0 6px ${cursor.color}40)` }}
              />
              {/* Name label - Figma style */}
              <div
                className="absolute start-4 top-4 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[11px] font-medium shadow-lg"
                style={{
                  backgroundColor: cursor.color,
                  color: '#000',
                  boxShadow: `0 2px 8px ${cursor.color}50`,
                }}
              >
                {cursor.name}
              </div>
            </div>
          </div>
        )
      })}

      {/* Wandering cursor - Luna moves around more dramatically */}
      <div
        className="absolute animate-cursor-wander transition-opacity duration-1000"
        style={{
          opacity: isVisible ? 1 : 0,
          transitionDelay: `${WANDERER.delay}s`,
        }}
      >
        <MousePointer2
          width={20}
          height={20}
          fill={WANDERER.color}
          color={WANDERER.color}
          className="drop-shadow-lg"
          style={{ filter: `drop-shadow(0 0 8px ${WANDERER.color}60)` }}
        />
        <div
          className="absolute start-4 top-4 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[11px] font-medium shadow-lg"
          style={{
            backgroundColor: WANDERER.color,
            color: '#000',
            boxShadow: `0 2px 8px ${WANDERER.color}50`,
          }}
        >
          {WANDERER.name}
        </div>
      </div>
    </div>
  )
}
